import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Select, DatePicker, Tag, Card, message } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  getUsageEventsApi,
  getUsageSummaryApi,
} from "../../../services/usage.services";
import UsageSummaryCards from "./UsageSummaryCards";
import UsageTable from "./UsageTable";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import { getFacilityListApi } from "../../../services/facilities.services";
import inputStyles from "../../../styles/Custom/Input.module.css";
import useQuery from "../../../hooks/useQuery";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../helpers/urlFilters";

const { Option } = Select;
const { RangePicker } = DatePicker;

const TAB_FILTERS = ["Whatsapp", "AI Requests", "SMS", "Email"];

const TAB_COLORS = {
  Whatsapp: { bg: "#ECFDF3", text: "#067647", border: "#ABEFC6" },
  "AI Requests": { bg: "#F4F3FF", text: "#5925DC", border: "#D9D6FE" },
  SMS: { bg: "#EFF8FF", text: "#175CD3", border: "#B2DDFF" },
  Email: { bg: "#FEF6EE", text: "#B93815", border: "#F9DBAF" },
};

// Map period value → number of days (for date range display)
const PERIOD_DAYS = {
  "1w": 7,
  "1m": 30,
  "3m": 90,
};

// Map tab label → API "kind" value
const TAB_TO_KIND = {
  Whatsapp: "whatsapp",
  "AI Requests": "ai",
  SMS: "sms",
  Email: "email",
};

const KIND_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_KIND).map(([tab, kind]) => [kind, tab])
);

/**
 * Compute [startDate, endDate] dayjs objects for a preset period.
 * endDate is today; startDate is N-1 days before today (inclusive range).
 */
const getPresetRange = (period) => {
  const days = PERIOD_DAYS[period];
  if (!days) return [null, null];
  const end = dayjs().endOf("day");
  const start = dayjs()
    .subtract(days - 1, "day")
    .startOf("day");
  return [start, end];
};

const UsageOverview = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const defaultFacilityId =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const period = params?.period || "1w";
  const isCustom = period === "custom";
  const currentPage = parsePageParam(params);
  const facilityId = params?.facility
    ? Number(params.facility)
    : defaultRole === "Facility_Admin"
      ? defaultFacilityId
      : null;
  const activeTabs = useMemo(() => {
    if (!params?.kind) return [];
    return params.kind
      .split(",")
      .map((kind) => KIND_TO_TAB[kind.trim()])
      .filter(Boolean);
  }, [params?.kind]);
  const customRange = useMemo(
    () =>
      isCustom
        ? [
            params?.start_date ? dayjs(params.start_date) : null,
            params?.end_date ? dayjs(params.end_date) : null,
          ]
        : [null, null],
    [isCustom, params?.start_date, params?.end_date]
  );

  const [eventsData, setEventsData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [facilityList, setFacilityList] = useState([]);

  // ── Derived date range for the "display pill" ──────────────────────────────
  const [presetStart, presetEnd] = isCustom
    ? [null, null]
    : getPresetRange(period);

  const displayStart = isCustom ? customRange[0] : presetStart;
  const displayEnd = isCustom ? customRange[1] : presetEnd;

  const formatDate = (d) => (d ? d.format("DD MMM YYYY") : "—");
  // ── Build API params from current filter state ─────────────────────────────
  const buildParams = useCallback(() => {
    const params = {};

    // period / dates
    if (isCustom) {
      params.period = "custom";
      if (customRange[0])
        params.start_date = customRange[0].format("YYYY-MM-DD");
      if (customRange[1]) params.end_date = customRange[1].format("YYYY-MM-DD");
    } else {
      params.period = period; // "1w" | "1m" | "3m"
    }

    // facility
    if (facilityId) params.facility_id = facilityId;
    if (defaultFacilityId !== "null") params.facility_id = defaultFacilityId;

    // kind — omit if all selected (no filter needed) or none selected
    if (activeTabs.length > 0 && activeTabs.length < TAB_FILTERS.length) {
      params.kind = activeTabs.map((tab) => TAB_TO_KIND[tab]);
    }

    return params;
  }, [period, isCustom, customRange, facilityId, activeTabs, defaultFacilityId]);

  // ── Fetch summary when filters change (page & kind excluded) ─────────────
  useEffect(() => {
    if (isCustom && (!customRange[0] || !customRange[1])) return;

    const params = buildParams();
    const { kind: _kind, ...summaryParams } = params; // strip kind
    getUsageSummaryApi(summaryParams)
      .then((res) => setSummaryData(res?.data))
      .catch((err) => message.error(err?.response?.data?.message));
  }, [buildParams, isCustom, customRange]);

  // ── Fetch events when filters or page changes (kind included) ────────────
  useEffect(() => {
    if (isCustom && (!customRange[0] || !customRange[1])) return;

    const params = buildParams();
    getUsageEventsApi({ ...params, page: currentPage })
      .then((res) => setEventsData(res?.data))
      .catch((err) => console.log(err?.response));
  }, [buildParams, isCustom, customRange, currentPage]);

  // ── Fetch facility list once ──────────────────────────────────────────────
  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        setFacilityList(res?.data);
      })
      .catch((err) => console.log(err));
  }, []);

  const handlePeriodChange = (value) => {
    updateUrlSearchParams(navigate, {
      period: value,
      start_date: null,
      end_date: null,
      page: 1,
    });
  };

  const handleCustomRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) {
      updateUrlSearchParams(navigate, {
        start_date: null,
        end_date: null,
        page: 1,
      });
      return;
    }
    updateUrlSearchParams(navigate, {
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
      page: 1,
    });
  };

  const handleFacilityChange = (value) => {
    updateUrlSearchParams(navigate, {
      facility: value === "All" ? null : value,
      page: 1,
    });
  };

  const handleTabToggle = (tab) => {
    const allSelected = activeTabs.length === 0;
    const isActive = allSelected || activeTabs.includes(tab);

    let nextTabs;
    if (allSelected) {
      nextTabs = TAB_FILTERS.filter((t) => t !== tab);
    } else if (isActive) {
      nextTabs = activeTabs.filter((t) => t !== tab);
    } else {
      nextTabs = [...activeTabs, tab];
    }

    const kindParam =
      nextTabs.length > 0 && nextTabs.length < TAB_FILTERS.length
        ? nextTabs.map((t) => TAB_TO_KIND[t]).join(",")
        : null;

    updateUrlSearchParams(navigate, { kind: kindParam, page: 1 });
  };

  const handlePageChange = (page) => {
    updateUrlSearchParams(navigate, { page });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Top filters row ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        {/* Facility selector (hidden for Facility_Admin if needed) */}
        {defaultRole !== "Facility_Admin" && (
          <Select
            value={facilityId ?? "All"}
            style={{ width: 250 }}
            onChange={handleFacilityChange}
            className={inputStyles.mdSelect}
            disabled={facilityList?.length === 1}
          >
            {facilityList?.length > 1 && <Option value="All">All </Option>}
            {facilityList?.map((f) => (
              <Option key={f.id} value={f.id}>
                {f.name}
              </Option>
            ))}
          </Select>
        )}

        {/* Period selector */}
        <Select
          value={period}
          style={{ width: 150 }}
          onChange={handlePeriodChange}
          className={inputStyles.mdSelect}
        >
          <Option value="1w">Last 7 days</Option>
          <Option value="1m">Last 30 days</Option>
          <Option value="3m">Last 90 days</Option>
          <Option value="custom">Custom</Option>
        </Select>

        {/* Date display: RangePicker for custom, disabled pill for presets */}
        {isCustom ? (
          <RangePicker
            className={inputStyles.mdDateInput}
            value={customRange[0] && customRange[1] ? customRange : undefined}
            onChange={handleCustomRangeChange}
            format="DD MMM YYYY"
            allowClear
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
          />
        ) : (
          <div
            style={{
              borderRadius: "8px",
              display: "flex",
              padding: "6px 14px",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              border: "1px solid #d0d5dd",
              width: "fitContent",
              height: "auto",
              background: "rgb(235 235 235)",
            }}
          >
            <CalendarOutlined style={{ color: "#6b7280", fontSize: 13 }} />
            <span>
              {formatDate(displayStart)} – {formatDate(displayEnd)}
            </span>
          </div>
        )}
      </div>

      {/* ── Summary cards ── */}
      <UsageSummaryCards summaryData={summaryData} />

      {/* ── Usage Data header + tab filters ── */}
      <UsageTable
        defaultRole={defaultRole}
        eventsData={eventsData}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        TAB_COLORS={TAB_COLORS}
        TAB_TO_KIND={TAB_TO_KIND}
        TAB_FILTERS={TAB_FILTERS}
        titleData={() => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
              Usage Data
            </span>

            <div style={{ display: "flex", gap: 6 }}>
              {TAB_FILTERS.map((tab) => {
                const isActive =
                  activeTabs.length === 0 || activeTabs.includes(tab);
                const col = TAB_COLORS[tab];
                return (
                  <Tag.CheckableTag
                    key={tab}
                    checked={isActive}
                    onChange={() => handleTabToggle(tab)}
                    style={{
                      background: isActive ? col.bg : "transparent",
                      color: col.text,
                      border: `1px solid ${col.border}`,
                      borderRadius: 10,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {tab}
                  </Tag.CheckableTag>
                );
              })}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default UsageOverview;
